/**
 * Session state — port of rent_management_app/lib/providers/auth_provider.dart's
 * session half (login / register / logout / restore-from-storage).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import * as authApi from '../api/endpoints/auth';
import { setUnauthorizedHandler } from '../api/client';
import {
  clearSession,
  getAccessToken,
  getRefreshToken,
  getStoredUser,
  setStoredUser,
  setTokens,
} from './storage';
import type { User } from '../types/user';

export interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (payload: authApi.RegisterRequest) => Promise<User>;
  logout: () => Promise<void>;
  /** Merges a partial update (e.g. from `PATCH auth/me/`) into the cached session user. */
  updateUser: (patch: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Restores a session from localStorage.
 *
 * Both a token and a cached user are required. A token with no user would
 * render shells with no name/role to branch on; a user with no token would fail
 * every request. Either half alone is a corrupt session, so it is dropped.
 *
 * This is SYNCHRONOUS (localStorage is), which is why there is no `loading`
 * state on the context. The Flutter app needs a splash screen here because
 * flutter_secure_storage is async; the web SPA does not, and adding one would
 * introduce a redirect race on first paint for no benefit.
 */
function restoreSession(): User | null {
  const token = getAccessToken();
  const user = getStoredUser();
  if (token === null || user === null) {
    if (token !== null || user !== null) clearSession();
    return null;
  }
  return user;
}

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [user, setUser] = useState<User | null>(restoreSession);

  // Lets the api client's failed-refresh path drop React state too, not just
  // localStorage — see setUnauthorizedHandler in src/api/client.ts.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
    });
    return () => {
      setUnauthorizedHandler(null);
    };
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<User> => {
    const data = await authApi.login({ email, password });
    setTokens({ access: data.access, refresh: data.refresh });
    setStoredUser(data.user);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload: authApi.RegisterRequest): Promise<User> => {
    const data = await authApi.register(payload);
    setTokens({ access: data.access, refresh: data.refresh });
    setStoredUser(data.user);
    setUser(data.user);
    return data.user;
  }, []);

  const updateUser = useCallback((patch: Partial<User>): void => {
    setUser((previous) => {
      if (previous === null) return previous;
      const updated = { ...previous, ...patch };
      setStoredUser(updated);
      return updated;
    });
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    const refresh = getRefreshToken();
    if (refresh !== null) {
      try {
        // Best-effort: blacklists the refresh token server-side. A failure here
        // must not strand the user in a half-logged-in state, so the local
        // session is cleared either way.
        await authApi.logout(refresh);
      } catch {
        /* already expired, already blacklisted, or offline */
      }
    }
    clearSession();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated: user !== null, login, register, logout, updateUser }),
    [user, login, register, logout, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthContext };
